'use client';

import type { ReactNode } from 'react';
import type { ToolUIPart } from 'ai';

import { MessageResponse } from '../ui/Message/Message';
import { ToolPart } from '../ui/Tool/ToolPart';

import type { MessagePart } from './types';
import { isTextPart, isStaticToolPart } from './types';
import { getToolNameFromToolPart } from './toolParts';

export type ToolPartRendererArgs = {
  toolName: string;
  toolCallId: string;
  state: ToolUIPart['state'];
  input: unknown;
  output?: unknown;
};

export type ToolPartRenderer = (args: ToolPartRendererArgs) => ReactNode;

export type RenderMessagePartsOptions = {
  toolPartRenderers?: Readonly<Record<string, ToolPartRenderer>>;
};

/** Render a single message part, or null when it has no body of its own. */
function renderPart(
  part: MessagePart,
  key: string,
  options?: RenderMessagePartsOptions
): ReactNode {
  if (isTextPart(part)) {
    return <MessageResponse key={key}>{part.text}</MessageResponse>;
  }

  if (isStaticToolPart(part)) {
    const toolPart = part as unknown as ToolUIPart;
    const toolName = getToolNameFromToolPart(toolPart);

    return (
      <ToolPart
        key={key}
        toolName={toolName}
        toolCallId={toolPart.toolCallId}
        state={toolPart.state}
        input={toolPart.input}
        output={toolPart.state === 'output-available' ? toolPart.output : undefined}
        customRenderer={options?.toolPartRenderers?.[toolName]}
      />
    );
  }

  // Reasoning renders as its own timeline step in the message row.
  return null;
}

export function renderMessageParts(
  parts: MessagePart[],
  messageId: string,
  options?: RenderMessagePartsOptions
): ReactNode[] {
  return parts
    .map((part, index) => renderPart(part, `${messageId}-${index}`, options))
    .filter((node) => node !== null);
}
